import { useEffect, useRef, useState, useCallback } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Nav } from "./components/Nav";
import { CartDrawer } from "./components/CartDrawer";
import { CartBar } from "./components/CartBar";
import { Footer } from "./components/Footer";
import Home from "./pages/Home";
import FrenchPage from "./pages/FrenchPage";
import ArabianPage from "./pages/ArabianPage";
import BakhoorPage from "./pages/BakhoorPage";
import PaymentResult from "./pages/PaymentResult";
import AdminPage from "./pages/AdminPage";
import TrackPage from "./pages/TrackPage";
import ShopPage from "./pages/ShopPage";
import ProductPage from "./pages/ProductPage";
import { CatalogProvider } from "./catalog";
import { AboutPage, ContactPage, PrivacyPage, TermsPage, ShippingPolicyPage, RefundPolicyPage } from "./pages/InfoPages";
import { LangContext, makeT, useLang } from "./i18n";

function Shell() {
    const { t } = useLang();
    const routerNav = useNavigate();
    const location = useLocation();
    const [cart, setCart] = useState([]);
    const [cartOpen, setCartOpen] = useState(false);
    const lenisRef = useRef(null);

    useEffect(() => {
        const lenis = new Lenis({ duration: 1.25, smoothWheel: true });
        lenisRef.current = lenis;
        let raf;
        const loop = (time) => {
            lenis.raf(time);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(raf);
            lenis.destroy();
        };
    }, []);

    useEffect(() => {
        lenisRef.current?.scrollTo(0, { immediate: true });
        window.scrollTo(0, 0);
    }, [location.pathname]);

    const navigate = useCallback(
        (id) => {
            if (id === "top") {
                if (location.pathname !== "/") routerNav("/");
                setTimeout(() => lenisRef.current?.scrollTo(0, { immediate: true }), 120);
                return;
            }
            if (id === "shop") return routerNav("/shop");
            if (id === "french-series" || id === "french-ice") return routerNav("/french");
            if (id === "arabian-series") return routerNav("/arabian");
            if (id === "bakhoor-collection") return routerNav("/bakhoor");
            if (location.pathname !== "/") {
                routerNav("/");
                setTimeout(() => lenisRef.current?.scrollTo(`#${id}`, { offset: -60 }), 500);
            } else {
                lenisRef.current?.scrollTo(`#${id}`, { offset: -60 });
            }
        },
        [location.pathname, routerNav]
    );

    const addToCart = (product) => {
        setCart((c) => {
            const found = c.find((i) => i.id === product.id);
            if (found) return c.map((i) => (i.id === product.id ? { ...i, qty: i.qty + 1 } : i));
            return [...c, { ...product, qty: 1 }];
        });
        toast.success(`${product.name} ${t("toast.added")}`);
    };

    const changeQty = (id, delta) =>
        setCart((c) => c.map((i) => (i.id === id ? { ...i, qty: Math.max(1, i.qty + delta) } : i)));

    const removeItem = (id) => setCart((c) => c.filter((i) => i.id !== id));

    return (
        <div className="grain bg-ivory min-h-screen">
            <Nav cartCount={cart.reduce((s, i) => s + i.qty, 0)} onCartOpen={() => setCartOpen(true)} onNavigate={navigate} />
            <Routes>
                <Route path="/" element={<Home onNavigate={navigate} />} />
                <Route path="/french" element={<FrenchPage onAdd={addToCart} />} />
                <Route path="/shop" element={<ShopPage onAdd={addToCart} />} />
                <Route path="/product/:slug" element={<ProductPage onAdd={addToCart} />} />
                <Route path="/arabian" element={<ArabianPage />} />
                <Route path="/bakhoor" element={<BakhoorPage onAdd={addToCart} />} />
                <Route path="/payment/success" element={<PaymentResult variant="success" />} />
                <Route path="/payment/cancel" element={<PaymentResult variant="cancel" />} />
                <Route path="/admin" element={<AdminPage />} />
                <Route path="/admin/*" element={<AdminPage />} />
                <Route path="/track" element={<TrackPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/shipping-policy" element={<ShippingPolicyPage />} />
                <Route path="/refund-policy" element={<RefundPolicyPage />} />
                <Route path="*" element={<Home onNavigate={navigate} />} />
            </Routes>
            <Footer />
            <CartBar
                items={cart}
                open={cartOpen}
                onOpen={() => setCartOpen(true)}
            />
            <CartDrawer
                open={cartOpen}
                onClose={() => setCartOpen(false)}
                cart={cart}
                onQty={changeQty}
                onRemove={removeItem}
                onClear={() => setCart([])}
            />
            <Toaster position="bottom-right" toastOptions={{ style: { background: "#1A1814", color: "#DFCC9D", border: "1px solid rgba(197,160,89,0.4)", borderRadius: 0 } }} />
        </div>
    );
}

export default function App() {
    const [lang, setLang] = useState("en");
    useEffect(() => {
        document.documentElement.setAttribute("dir", lang === "ar" ? "rtl" : "ltr");
        document.documentElement.setAttribute("lang", lang);
    }, [lang]);
    return (
        <LangContext.Provider value={{ lang, setLang, t: makeT(lang) }}>
            <div dir={lang === "ar" ? "rtl" : "ltr"} className={lang === "ar" ? "lang-ar" : lang === "bn" ? "lang-bn" : ""}>
                <BrowserRouter>
                    <CatalogProvider>
                        <Shell />
                    </CatalogProvider>
                </BrowserRouter>
            </div>
        </LangContext.Provider>
    );
}
